/* eslint-disable react/no-unescaped-entities */
/**
 * Bridge Screen 1 — shown after The Me I See is completed, before HWYCF unlocks.
 * Route: /coaching/bridge/me-i-see-to-hwycf
 *
 * Locked copy from Dr. Christy Vonta — do NOT modify. Users reach this
 * screen either from the Me I See certificate "Continue → what's next"
 * button, or directly. Only relevant when Me I See is completed.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Lock, ArrowRight } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function BridgeMeISee() {
  const [unlocked, setUnlocked] = useState(null);
  const email = localStorage.getItem("me_i_see_email") ||
                localStorage.getItem("coaching_dashboard_email") || "";

  useEffect(() => {
    if (!email) { setUnlocked(false); return; }
    fetch(`${API}/api/coaching/dashboard/for-client`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    }).then((r) => r.json()).then((d) => {
      const hwycf = (d.programs || []).find((p) => p.program_slug === "healing-what-you-cannot-feel");
      setUnlocked(!!(hwycf && hwycf.enrolled && hwycf.content_unlocked));
    }).catch(() => setUnlocked(false));
  }, [email]);

  return (
    <div data-testid="bridge-me-i-see-screen" className="min-h-screen"
         style={{ background: "linear-gradient(180deg,#faf7f5 0%,#fff 100%)",
                  fontFamily: "'Inter', sans-serif" }}>
      <div className="max-w-3xl mx-auto px-6 py-16">
        <div className="text-[11px] uppercase tracking-[0.32em]"
             style={{ color: "#5B2C83", fontWeight: 700 }}>
          Bridge · Program 01 → Program 02
        </div>
        <h1 className="mt-4 text-4xl sm:text-5xl leading-tight"
            style={{ fontFamily: "'Playfair Display', serif", color: "#111" }}>
          You Have Seen Her. Now It's Time to Heal Her.
        </h1>
        <div className="mt-8 space-y-5 text-slate-700 leading-relaxed" style={{ fontSize: 16.5 }} data-testid="bridge-body">
          <p>
            Eight weeks ago, you began by asking who you'd become underneath every role, every mask, every version of yourself built to survive. You've named the roles. You've questioned the story you were told about your worth. You've written a Future Self Profile and an Identity Declaration in your own words.
          </p>
          <p>
            That clarity was never the destination — it was the map. You now know who you are. What you haven't done yet is deal with what shaped her.
          </p>
          <p>
            That's what Healing What You Cannot Feel is for. It is not a smaller task than the one you just finished — it's the one this entire journey has been quietly pointing toward. You can't build a lasting identity on top of trauma you've never opened. This next phase is where you finally go back for what you buried, so nothing you just discovered about yourself has to keep competing with what you never processed.
          </p>
          <p style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, color: "#5B2C83", fontStyle: "italic" }}>
            You didn't come this far to stop at recognition.
          </p>
        </div>

        <div className="mt-10">
          {unlocked === null ? (
            <button disabled className="rounded-full px-6 py-3 text-sm text-white opacity-50"
                    style={{ background: "#5B2C83" }}>
              Checking…
            </button>
          ) : unlocked ? (
            <Link to="/coaching/hwycf/login" data-testid="bridge-primary-cta">
              <button className="rounded-full px-7 py-4 text-sm font-medium text-white inline-flex items-center gap-2"
                      style={{ background: "linear-gradient(135deg,#5B2C83,#9B59D9)",
                               boxShadow: "0 0 24px rgba(155,89,217,0.4)" }}>
                Begin Healing What You Cannot Feel <ChevronRight className="w-4 h-4" />
              </button>
            </Link>
          ) : (
            <div className="rounded-2xl border p-5 flex items-start gap-3"
                 style={{ borderColor: "#ddd", background: "#f6f4f0" }}
                 data-testid="bridge-locked-notice">
              <Lock className="w-5 h-5 mt-0.5 shrink-0" style={{ color: "#5B2C83" }} />
              <div>
                <div className="text-sm font-medium text-slate-900">
                  Unlocks now that you've completed The Me I See
                </div>
                <div className="mt-1 text-xs text-slate-600">
                  Head back to your coaching dashboard to enroll in Healing What You Cannot Feel and begin.
                </div>
                <Link to="/coaching/dashboard" data-testid="bridge-dashboard-link">
                  <button className="mt-3 rounded-full px-5 py-2.5 text-xs font-medium text-white inline-flex items-center gap-1"
                          style={{ background: "#5B2C83" }}>
                    Open coaching dashboard <ArrowRight className="w-3 h-3" />
                  </button>
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
